import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { getBidList } from "../utils/api";
import { useAuth } from "../contexts/AuthContext";

const toMatchKey = (matchId) =>
  matchId === null || matchId === undefined || matchId === ""
    ? null
    : String(matchId);

const DriverBidsContext = createContext(null);

/**
 * Prefetch driver/dispatcher bids once and share them across platforms.
 * Placing a bid only upserts local state — no per-load getBid calls.
 */
export const DriverBidsProvider = ({ children }) => {
  const { user } = useAuth();
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadedForUserId, setLoadedForUserId] = useState(null);
  const requestIdRef = useRef(0);

  const refresh = useCallback(async () => {
    if (!user?.id) {
      setBids([]);
      setLoadedForUserId(null);
      return [];
    }

    const requestId = ++requestIdRef.current;
    setLoading(true);
    try {
      // Drivers: own bids. Dispatchers/Admins: all bids.
      const list =
        user.user_type === "Dispatcher" ||
        user.user_type === "Admin" ||
        user.user_type === "SuperAdmin"
          ? await getBidList()
          : await getBidList(user.id);

      if (requestId !== requestIdRef.current) {
        return [];
      }

      const next = Array.isArray(list) ? list : [];
      setBids(next);
      setLoadedForUserId(user.id);
      return next;
    } catch (error) {
      console.error("Failed to load bids in advance:", error);
      if (requestId === requestIdRef.current) {
        setBids([]);
        setLoadedForUserId(user.id);
      }
      return [];
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [user?.id, user?.user_type]);

  useEffect(() => {
    if (!user?.id) {
      setBids([]);
      setLoadedForUserId(null);
      return undefined;
    }

    // Defer bid prefetch so it does not compete with first paint / dashboard tabs.
    let cancelled = false;
    const run = () => {
      if (!cancelled) refresh();
    };
    let idleId;
    let timeoutId;
    if (typeof window !== "undefined" && window.requestIdleCallback) {
      idleId = window.requestIdleCallback(run, { timeout: 2500 });
    } else {
      timeoutId = setTimeout(run, 300);
    }

    return () => {
      cancelled = true;
      if (idleId != null && window.cancelIdleCallback) {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId != null) clearTimeout(timeoutId);
    };
  }, [refresh, user?.id]);

  const getBidForMatch = useCallback(
    (matchId) => {
      const matchKey = toMatchKey(matchId);
      if (!matchKey) return null;

      if (user?.user_type === "Driver" && user?.id != null) {
        return (
          bids.find(
            (item) =>
              String(item.match_id) === matchKey &&
              Number(item.bidder_id) === Number(user.id)
          ) || null
        );
      }

      return bids.find((item) => String(item.match_id) === matchKey) || null;
    },
    [bids, user?.id, user?.user_type]
  );

  const upsertBid = useCallback((nextBid) => {
    if (!nextBid) return;
    // Invalidate in-flight refreshes so they cannot overwrite this update.
    requestIdRef.current += 1;
    setBids((prev) => {
      const matchKey = toMatchKey(nextBid.match_id);
      return [
        ...prev.filter(
          (item) =>
            item.id !== nextBid.id &&
            !(
              matchKey &&
              String(item.match_id) === matchKey &&
              Number(item.bidder_id) === Number(nextBid.bidder_id)
            )
        ),
        nextBid,
      ];
    });
  }, []);

  const value = useMemo(
    () => ({
      bids,
      loading,
      ready: loadedForUserId != null && loadedForUserId === user?.id,
      refresh,
      getBidForMatch,
      upsertBid,
    }),
    [
      bids,
      loading,
      loadedForUserId,
      user?.id,
      refresh,
      getBidForMatch,
      upsertBid,
    ]
  );

  return (
    <DriverBidsContext.Provider value={value}>
      {children}
    </DriverBidsContext.Provider>
  );
};

export const useDriverBids = () => {
  const context = useContext(DriverBidsContext);
  if (!context) {
    throw new Error("useDriverBids must be used within DriverBidsProvider");
  }
  return context;
};

/**
 * Look up a prefetched bid for a match id. No network request.
 */
export const useExistingBid = (matchId) => {
  const { getBidForMatch, upsertBid, loading, refresh } = useDriverBids();
  const bid = getBidForMatch(matchId);

  return {
    bid,
    setBid: upsertBid,
    loading,
    refresh,
  };
};

export const buildLaneMatchId = (...parts) =>
  parts
    .map((part) =>
      part === null || part === undefined ? "" : String(part).trim()
    )
    .filter(Boolean)
    .join("|");

/** Normalize API bid payloads so UI can switch to status after save. */
export const withImmediateBidFields = (
  bid,
  { matchId, amount, status = "PENDING", platform } = {}
) => ({
  ...bid,
  match_id:
    bid?.match_id != null && bid.match_id !== ""
      ? String(bid.match_id)
      : String(matchId ?? ""),
  amount: bid?.amount ?? amount,
  status: bid?.status || status,
  platform: bid?.platform || platform,
  created_at: bid?.created_at || new Date().toISOString(),
});
